/**
 * CritiqueSessionList Component
 * Feature: 055-my-critiques-page, 057-critique-quick-create
 *
 * Displays a vertical list of critique sessions owned by the current user.
 * Each session shows collection info, session title, and recent comments.
 *
 * Features (057):
 * - "Create Critique" button with drag-and-drop support
 * - Quick critique session creation (collection + session in one action)
 */

import { useNavigate } from 'react-router-dom';
import { useCritiqueSessionsGraphQL } from '../../../graphql/hooks/useCritiquesGraphQL';
import { useCreateCollectionGraphQL, useCreateCritiqueSessionGraphQL } from '../../../hooks/graphql/useCollectionMutationsGraphQL';
import { useCreateWithFiles } from '../../../hooks/useCreateWithFiles';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useAuth } from '../../../services/auth';
import { CritiqueSessionListItem } from '../CritiqueSessionListItem/CritiqueSessionListItem';
import { Button } from '@/components/atoms';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '@/components/PageHeader';
import { CritiqueIcon, FileIcon, FolderIcon, PhotoIcon } from '@/components/icons';
import { FileDropZone } from '@/components/photos/FileDropZone';
import { AddExistingPhotosModal } from '@/components/composed';
import { collectionEditUrl } from '@/utils/urls';
import { ACCEPTED_FILE_INPUT, filterValidFiles } from '@/utils/fileValidation';
import { generateCollectionName, generateCritiqueSessionTitle } from '@/utils/collectionName';
import { useApolloClient } from '@apollo/client';

import logger from '../../../utils/logger';

export interface CritiqueSessionListProps {
  className?: string;
}

/**
 * List container for critique sessions
 *
 * Fetches and displays all critique sessions for the current user.
 * Uses CollectionAndDetailView2 layout pattern (w-full h-full min-h-0 overflow-y-auto).
 *
 * @param className - Optional CSS classes for styling
 *
 * @example
 * ```tsx
 * <CritiqueSessionList className="bg-gray-100" />
 * ```
 */
export function CritiqueSessionList({ className = '' }: CritiqueSessionListProps) {
  const { sessions, hasMore, loading, loadMore, error } = useCritiqueSessionsGraphQL();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createCollection, loading: collectionLoading } = useCreateCollectionGraphQL();
  const { createCritiqueSession, loading: sessionLoading } = useCreateCritiqueSessionGraphQL();
  const apolloClient = useApolloClient();

  // Infinite scroll
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    loading,
  });

  // Handle creation of collection + session and navigation
  const handleCreateCritique = async (files?: File[]) => {
    if (!user) return;

    const collectionName = generateCollectionName(user.username);
    const sessionTitle = generateCritiqueSessionTitle(collectionName);

    try {
      logger.info('CritiqueSessionList', 'Creating collection and session via GraphQL', { collectionName, sessionTitle });

      // Step 1: Create collection via GraphQL
      const collection = await createCollection({ name: collectionName });

      if (!collection?.globalId) {
        throw new Error('Collection created without globalId');
      }

      // Step 2: Create session for collection via GraphQL
      // Session is created but we navigate to collection edit page (not critique page)
      // This ensures the session exists for later critique workflow
      await createCritiqueSession({
        collectionId: collection.globalId,
        title: sessionTitle
      });

      // Refetch GraphQL queries
      apolloClient.refetchQueries({ include: ['Collections', 'CritiqueSessions'] });

      // Step 3: Navigate to collection EDIT page with files (not critique page)
      // The collection editor handles file uploads, then users can navigate to critique mode
      const slug = collection.name ? collection.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
      navigate(collectionEditUrl(collection.globalId, slug), {
        state: { initialFiles: files }
      });
    } catch (error) {
      logger.error('CritiqueSessionList', 'Failed to create critique:', error);
      // Error state will be shown on button via isPending/isError from mutation
    }
  };

  // Handle creation of collection + session with existing photos
  const handleCreateCritiqueWithExistingPhotos = async (photoIds: string[]) => {
    if (!user || photoIds.length === 0) return;

    const collectionName = generateCollectionName(user.username);
    const sessionTitle = generateCritiqueSessionTitle(collectionName);

    try {
      logger.info('CritiqueSessionList', 'Creating collection and session with existing photos via GraphQL', { collectionName, sessionTitle, photoCount: photoIds.length });

      // Step 1: Create collection via GraphQL
      const collection = await createCollection({ name: collectionName });

      if (!collection?.globalId) {
        throw new Error('Collection created without globalId');
      }

      // Step 2: Create session for collection via GraphQL
      await createCritiqueSession({
        collectionId: collection.globalId,
        title: sessionTitle
      });

      // Refetch GraphQL queries
      apolloClient.refetchQueries({ include: ['Collections', 'CritiqueSessions'] });

      // Step 3: Navigate to collection EDIT page with photo IDs
      const slug = collection.name ? collection.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
      navigate(collectionEditUrl(collection.globalId, slug), {
        state: { initialPhotoIds: photoIds }
      });
    } catch (error) {
      logger.error('CritiqueSessionList', 'Failed to create critique with existing photos:', error);
    }
  };

  // Combined loading state
  const isMutating = collectionLoading || sessionLoading;

  // Initialize file upload hook (with folder and existing photos support)
  const {
    fileInputRef,
    setFolderInputRef,
    menuRef,
    handleButtonClick,
    handleSelectFiles,
    handleSelectFolder,
    handleFilesDropped,
    handleInvalidFiles,
    isPending,
    showUploadMenu,
    folderSupported,
    showFolderOption,
    showExistingPhotosOption,
    showExistingPhotosModal,
    handleSelectExistingPhotos,
    handleExistingPhotosSelected,
    handleCloseExistingPhotosModal
  } = useCreateWithFiles({
    onCreate: handleCreateCritique,
    onCreateWithExistingPhotos: handleCreateCritiqueWithExistingPhotos,
    disabled: !user || isMutating
  });

  const body = () => {
    // Initial loading state (no sessions yet)
    if (loading && sessions.length === 0) {
      logger.debug('CritiqueSessionList', 'Loading discussions...');
      return (
        <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center`}>
          <div className="text-gray-500">Loading discussions...</div>
        </div>
      );
    }

    // Error state
    if (error) {
      logger.error('CritiqueSessionList', 'Failed to load discussions', error);
      return (
        <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center`}>
          <div className="text-red-600">Failed to load discussions. Please try again.</div>
        </div>
      );
    }

    // Empty state
    if (sessions.length === 0) {
      logger.debug('CritiqueSessionList', 'No discussions found');
      return (
        <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center`}>
          <div className="text-center text-gray-500 px-4">
            <p className="text-lg font-medium mb-2">No discussions yet</p>
            <p className="text-sm">Create a collection and share it for feedback!</p>
          </div>
        </div>
      );
    }
    else {
      // Sessions list
      logger.info('CritiqueSessionList', `Rendering ${sessions.length} discussions`);
      return (
        <>
          {sessions.map((session) => (
            <CritiqueSessionListItem key={session.globalId} session={session} />
          ))}
          {/* Infinite scroll sentinel */}
          <div
            ref={sentinelRef}
            className="h-4"
            role="status"
            aria-live="polite"
            aria-label={loading && hasMore ? 'Loading more sessions' : undefined}
          />
          {loading && hasMore && (
            <div className="flex justify-center py-4">
              <div className="text-neutral-400">Loading more sessions...</div>
            </div>
          )}
        </>
      );
    }
  };


  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Hidden file input for file picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_INPUT}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) handleCreateCritique(files);
        }}
        multiple
        className="hidden"
      />
      {/* Hidden folder input for folder picker */}
      {folderSupported && (
        <input
          ref={setFolderInputRef}
          type="file"
          accept={ACCEPTED_FILE_INPUT}
          onChange={(e) => {
            const rawFiles = Array.from(e.target.files || []);
            // Apply file validation for folder selection (may contain non-image files)
            const { valid: files, invalid } = filterValidFiles(rawFiles);
            if (invalid.length > 0) {
              logger.info('CritiqueSessionList', `Filtered out ${invalid.length} invalid files from folder selection`);
            }
            if (files.length > 0) handleCreateCritique(files);
          }}
          className="hidden"
        />
      )}

      {/* Header with icon, title, and Create Discussion button */}
      <PageHeader>
        <PageHeaderLabel
          title="Discussions"
          icon={<CritiqueIcon size={32} />}
        />
        <PageHeaderCommandWell>
          {/* Create Critique Button with drag-and-drop support */}
          <FileDropZone
            onFilesDropped={handleFilesDropped}
            onInvalidFiles={handleInvalidFiles}
            disabled={isPending || !user}
            className="inline-block"
          >
            <div className="relative inline-block">
              <Button
                variant="primary"
                size="md"
                onClick={handleButtonClick}
                loading={isPending}
                disabled={isPending || !user}
              >
                {/* Camera icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  {/* Camera body */}
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  {/* Camera lens */}
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Start Discussion
                {showFolderOption && folderSupported && (
                  <span className="ml-1">▾</span>
                )}
              </Button>

              {/* Dropdown menu for file/folder/existing photos selection */}
              {showUploadMenu && (
                <div
                  ref={menuRef}
                  className="absolute right-0 mt-1 w-48 bg-surface-1 border border-border rounded-md shadow-lg z-50"
                >
                  <button
                    type="button"
                    onClick={handleSelectFiles}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-t-md flex items-center gap-2"
                  >
                    <FileIcon size={16} />
                    Select Files
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectFolder}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 flex items-center gap-2"
                  >
                    <FolderIcon size={16} />
                    Select Folder
                  </button>
                  {showExistingPhotosOption && (
                    <button
                      type="button"
                      onClick={handleSelectExistingPhotos}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-b-md flex items-center gap-2"
                    >
                      <PhotoIcon size={16} />
                      Existing Photos
                    </button>
                  )}
                </div>
              )}
            </div>
          </FileDropZone>
        </PageHeaderCommandWell>
      </PageHeader>

      {/* Session list */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {body()}
      </div>

      {/* Existing photos selection modal */}
      <AddExistingPhotosModal
        open={showExistingPhotosModal}
        onClose={handleCloseExistingPhotosModal}
        onAdd={handleExistingPhotosSelected}
        existingPhotoIds={[]}
      />
    </div>
  );
}
